<template>
  <div class="ikc">
    <b-overlay :show="loading && !loaded" rounded opacity="0.4" spinner-variant="primary">
      <b-row class="cc-kpis" :class="{ 'cc-dim': loading && loaded }">
        <!-- Hero: TOTAL deposits (incl. pending) + approved, net, sales/retention split, pending deposits -->
        <b-col xl="4" md="12" class="mb-2">
          <div class="kpi-card kpi-card--success cc-hero">
            <div class="cc-hero__body">
              <div class="cc-hero__totals">
                <button type="button" class="cc-hero__main" v-b-tooltip.hover
                        :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('depositsWithPending')">
                  <span class="kpi-card__title">{{ $t('mobileDash.totalDeposits') }}<i class="fas fa-expand cc-hero__expand"></i></span>
                  <span class="cc-hero__value" :class="{ 'ui-masked': hideAmounts }">{{ money(depositsWithPending) }}</span>
                  <span class="cc-hero__meta">
                    <span><i class="fa-regular fa-clock mr-25"></i>{{ $t('mobileDash.withPending') }}</span>
                  </span>
                </button>
                <button type="button" class="cc-hero__approved" v-b-tooltip.hover
                        :title="$t('salesDashboard.viewTransactions')" @click.stop="openKpiDrill('deposits')">
                  <span class="cc-hero__approved-label">
                    <i class="fa-solid fa-circle-check mr-25"></i>{{ $t('financeStatuses.APPROVED') }}
                    <i class="fas fa-expand cc-hero__expand ml-25"></i>
                  </span>
                  <span class="cc-hero__approved-value" :class="{ 'ui-masked': hideAmounts }">{{ money(k.totalDeposits) }}</span>
                  <span class="cc-hero__approved-meta">
                    <i class="fa-solid fa-hashtag mr-25"></i>{{ k.depositsCount || 0 }} {{ $t('mobileDash.drill.items') }}
                  </span>
                </button>
              </div>
              <button type="button" class="cc-hero__net" v-b-tooltip.hover
                      :title="$t('salesDashboard.viewTransactions')" @click.stop="openKpiDrill('net')">
                <i class="fa-solid fa-scale-balanced mr-25"></i>{{ $t('mobileDash.net') }}
                <b :class="{ 'ui-masked': hideAmounts }">{{ money(k.netDeposits) }}</b>
                <i class="fas fa-expand cc-hero__expand ml-25"></i>
              </button>

              <div class="cc-split">
                <div class="cc-split__bar">
                  <span class="cc-split__seg cc-split__seg--sales" :style="{ width: salesShare + '%' }"></span>
                </div>
                <div class="cc-split__legend">
                  <button type="button" class="cc-split__part" v-b-tooltip.hover
                          :title="$t('salesDashboard.viewTransactions')" @click="openDepartmentDrill('SALES')">
                    <i class="fa-solid fa-star mr-25"></i>{{ $t('mobileDash.sales') }}
                    <b :class="{ 'ui-masked': hideAmounts }">{{ money(k.salesDeposits) }}</b>
                    <span class="cc-split__pct">{{ Math.round(salesShare) }}%</span>
                  </button>
                  <button type="button" class="cc-split__part cc-split__part--right" v-b-tooltip.hover
                          :title="$t('salesDashboard.viewTransactions')" @click="openDepartmentDrill('RETENTION')">
                    <span class="cc-split__pct">{{ Math.round(100 - salesShare) }}%</span>
                    <b :class="{ 'ui-masked': hideAmounts }">{{ money(k.retentionDeposits) }}</b>
                    {{ $t('mobileDash.retention') }}<i class="fa-solid fa-coins ml-25"></i>
                  </button>
                </div>
              </div>

              <button type="button" class="cc-hero__pending" v-b-tooltip.hover
                      :title="$t('salesDashboard.viewTransactions')" @click="openPendingDrill('DEPOSIT')">
                <span class="cc-hero__pending-row">
                  <span class="cc-hero__pending-label"><i class="fa-regular fa-clock mr-25"></i>{{ $t('monetaryDashboard.pendingDeposits') }}</span>
                  <span class="cc-hero__pending-count">{{ k.pendingDepositsCount || 0 }}</span>
                  <span class="cc-hero__pending-amount" :class="{ 'ui-masked': hideAmounts }">{{ money(k.pendingDepositsAmount) }}</span>
                  <i class="fa-solid fa-chevron-right cc-hero__pending-arrow"></i>
                </span>
              </button>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- Pending review — the Finance Dashboard's review queue -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--pending kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openPendingReviewDrill">
            <div class="kpi-card__icon"><i class="fa-solid fa-magnifying-glass-dollar"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.pendingReview') }}</div>
              <div class="kpi-card__value">{{ k.pendingReviewCount || 0 }}</div>
              <div class="kpi-card__footer" :class="{ 'ui-masked': hideAmounts }">{{ money(k.pendingReviewAmount) }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--danger kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('withdrawals')">
            <div class="kpi-card__icon"><i class="fa-solid fa-arrow-up-from-bracket"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.withdrawals') }}</div>
              <div class="kpi-card__value" :class="{ 'ui-masked': hideAmounts }">{{ money(Math.abs(k.totalWithdrawals || 0)) }}</div>
              <div class="kpi-card__footer">{{ k.withdrawalsCount || 0 }} {{ $t('mobileDash.drill.items') }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- FTD — with the lead figures. With an agent filter, "leads" = leads those agents received. -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--warning kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('ftd')">
            <div class="kpi-card__icon"><i class="fa-solid fa-star"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.ftd') }}</div>
              <div class="kpi-card__value">{{ k.ftdCount || 0 }}</div>
              <div class="kpi-card__footer">
                <span :class="{ 'ui-masked': hideAmounts }">{{ money(k.ftdAmount) }}</span>
                <span class="cc-pct ml-50">{{ ftdOfLeadsPct }}% {{ $t('mobileDash.ofLeads') }}</span>
              </div>
              <div class="kpi-card__footer cc-kpi-sub">
                <span v-if="agentScoped"><i class="fa-solid fa-user-plus mr-25"></i>{{ k.clientsCount || 0 }} {{ $t('salesDashboard.received').toLowerCase() }}</span>
                <span v-else><i class="fa-solid fa-user-plus mr-25"></i>{{ k.clientsCount || 0 }} {{ $t('mobileDash.newClients').toLowerCase() }}</span>
                <span v-if="!agentScoped && k.duplicatedCount"> · <i class="fa-solid fa-copy mr-25"></i>{{ k.duplicatedCount }} {{ $t('affiliateDashboard.duplicated').toLowerCase() }}</span>
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--primary kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('std')">
            <div class="kpi-card__icon"><i class="fa-solid fa-medal"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.std') }}</div>
              <div class="kpi-card__value">{{ k.stdCount || 0 }}</div>
              <div class="kpi-card__footer" :class="{ 'ui-masked': hideAmounts }">{{ money(k.stdAmount) }}</div>
              <div class="kpi-card__footer"><span class="cc-pct">{{ stdOfFtdPct }}% {{ $t('monetaryDashboard.ofFtds') }}</span></div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--txf kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.txfHint')" @click="openKpiDrill('txf')">
            <div class="kpi-card__icon"><i class="fa-solid fa-file-invoice-dollar"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value" :class="{ 'ui-masked': hideAmounts }">{{ money(k.txfAmount) }}</div>
              <div class="kpi-card__footer">{{ k.txfCount || 0 }} {{ $t('mobileDash.drill.items') }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- Needs attention — pending withdrawals + FAKE deposits / withdrawals -->
    <div class="cc-watch mb-2">
      <div class="cc-watch__title">
        <i class="fa-solid fa-bell mr-25"></i>{{ $t('commandCenter.attention') }}
      </div>
      <div class="cc-watch__grid">
        <button type="button" class="cc-watch__tile cc-watch__tile--wd" v-b-tooltip.hover
                :title="$t('salesDashboard.viewTransactions')" @click="openPendingDrill('WITHDRAWAL')">
          <span class="cc-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('monetaryDashboard.pendingWithdrawals') }}</span>
          <span class="cc-watch__count">{{ k.pendingWithdrawalsCount || 0 }}</span>
          <span class="cc-watch__amount" :class="{ 'ui-masked': hideAmounts }">{{ money(Math.abs(k.pendingWithdrawalsAmount || 0)) }}</span>
        </button>
        <button type="button" class="cc-watch__tile cc-watch__tile--fake" v-b-tooltip.hover
                :title="$t('salesDashboard.viewTransactions')" @click="openFakeDrill('DEPOSIT')">
          <span class="cc-watch__badge">FAKE</span>
          <span class="cc-watch__label"><i class="fa-solid fa-arrow-down mr-25"></i>{{ $t('Fake Deposits') }}</span>
          <span class="cc-watch__count">{{ k.fakeDepositsCount || 0 }}</span>
          <span class="cc-watch__amount" :class="{ 'ui-masked': hideAmounts }">{{ money(k.fakeDeposits) }}</span>
        </button>
        <button type="button" class="cc-watch__tile cc-watch__tile--fake" v-b-tooltip.hover
                :title="$t('salesDashboard.viewTransactions')" @click="openFakeDrill('WITHDRAWAL')">
          <span class="cc-watch__badge">FAKE</span>
          <span class="cc-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('Fake Withdrawals') }}</span>
          <span class="cc-watch__count">{{ k.fakeWithdrawalsCount || 0 }}</span>
          <span class="cc-watch__amount" :class="{ 'ui-masked': hideAmounts }">{{ money(Math.abs(k.fakeWithdrawals || 0)) }}</span>
        </button>
      </div>
    </div>

    <!-- Transactions drill: the same modal (and skin) the Command Center uses -->
    <insight-transactions-modal ref="txModal" modal-class="cc-modal"/>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal';

/**
 * The Command Center KPI cards (hero + 6 cards + "needs attention" tiles) for
 * any scope — used by Agent Insights. Same markup/styles/drills as
 * CommandCenter.vue; the figures come from POST /dashboard/user-insights/kpis
 * (same backend KPI builder as the Command Center).
 *
 * scope = { range: {from, until}, orgIds, buIds } — the page's filter.
 * kpis.agentIds (from the backend) = the agents the figures were limited to;
 * the drills then list only those agents' transactions (depositor).
 */
export default {
  name: 'InsightsKpiCards',
  components: { InsightTransactionsModal },
  mixins: [mixinBase],
  props: {
    kpis: { type: Object, default: null },
    scope: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    hideAmounts: { type: Boolean, default: false },
  },
  computed: {
    k() { return this.kpis || {}; },
    loaded() { return this.kpis != null; },
    agentIds() { return Array.isArray(this.k.agentIds) ? this.k.agentIds : null; },
    agentScoped() { return this.agentIds != null; },
    depositsWithPending() {
      return (this.k.totalDeposits || 0) + (this.k.pendingDepositsAmount || 0);
    },
    salesShare() {
      const s = this.k.salesDeposits || 0;
      const r = this.k.retentionDeposits || 0;
      return s + r > 0 ? (s / (s + r)) * 100 : 0;
    },
    ftdOfLeadsPct() {
      const leads = this.k.clientsCount || 0;
      return leads ? ((this.k.ftdCount || 0) / leads * 100).toFixed(1) : '0';
    },
    stdOfFtdPct() {
      const ftd = this.k.ftdCount || 0;
      return ftd ? Math.round((this.k.stdCount || 0) / ftd * 100) : 0;
    },
  },
  methods: {
    money(v) { return this.convertToEURO(v || 0); },

    // ---- drills: identical filters to CommandCenter.vue, plus the agent scope ----
    kpiTxFilters(extra) {
      const s = this.scope || {};
      const filters = {
        type: 'DEPOSIT',
        action: ['CREDIT_LINE', 'TAXES'],
        status: ['APPROVED'],
        isTest: false,
        realType: ['REAL'],
        orgIds: (s.orgIds && s.orgIds.length) ? s.orgIds : null,
        businessUnit: (s.buIds && s.buIds.length) ? s.buIds : null,
        ...(extra || {}),
      };
      if (this.agentScoped) {
        // an empty agent list must match nothing, not everything
        filters.userIds = this.agentIds.length ? this.agentIds : ['0'];
      }
      const range = s.range || {};
      if (range.from) {
        filters.registrationTime = {
          start: this.$moment(range.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(range.until || range.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      return filters;
    },
    openDrill(title, extra) {
      this.$refs.txModal.show(title, this.kpiTxFilters(extra));
    },
    openKpiDrill(kind) {
      if (kind === 'depositsWithPending') {
        this.openDrill(this.$t('mobileDash.withPending'), { status: ['APPROVED', 'REQUEST', 'PENDING'] });
      } else if (kind === 'ftd') {
        this.openDrill(this.$t('mobileDash.ftd'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'std') {
        this.openDrill(this.$t('mobileDash.std'), { isStd: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'txf') {
        this.openDrill(this.$t('monetaryDashboard.txf'), { action: ['TAXES'] });
      } else if (kind === 'withdrawals') {
        this.openDrill(this.$t('mobileDash.withdrawals'), { type: 'WITHDRAWAL', action: ['CREDIT_LINE'] });
      } else if (kind === 'net') {
        this.openDrill(this.$t('mobileDash.net'), { type: null });
      } else {
        this.openDrill(this.$t('mobileDash.deposits'), {});
      }
    },
    openPendingDrill(type) {
      const label = type === 'DEPOSIT'
          ? this.$t('monetaryDashboard.pendingDeposits')
          : this.$t('monetaryDashboard.pendingWithdrawals');
      this.openDrill(label, { type, status: ['REQUEST', 'PENDING'], action: ['CREDIT_LINE'] });
    },
    openDepartmentDrill(department) {
      if (department === 'SALES') {
        this.openDrill(this.$t('mobileDash.sales'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else {
        this.openDrill(this.$t('mobileDash.retention'), { retentionBucket: true, action: ['CREDIT_LINE'] });
      }
    },
    openPendingReviewDrill() {
      this.openDrill(this.$t('finance.pendingReview'), { status: ['APPROVED', 'CANCELED'], financeStatus: ['PENDING'] });
    },
    openFakeDrill(type) {
      if (type === 'DEPOSIT') {
        this.openDrill(this.$t('Fake Deposits'), { realType: ['FAKE'] });
      } else {
        this.openDrill(this.$t('Fake Withdrawals'), { type: 'WITHDRAWAL', realType: ['FAKE'], action: ['CREDIT_LINE'] });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Copied from CommandCenter.vue (KPI row + attention tiles) — keep both in sync. */

.cc-dim { opacity: 0.55; transition: opacity 0.2s ease; }

/* ================= HERO ================= */
/* All KPI cards in the row share ONE height (the columns stretch, the cards fill them) */
.cc-kpis .kpi-card { height: 100%; min-height: 0; }
.cc-hero {
  align-items: flex-start;
  padding: 1rem 1.25rem 0.9rem;
  /* deeper green than the small deposit cards: white text + gold/amber accents stay readable */
  &.kpi-card--success { background: linear-gradient(135deg, #0ea371 0%, #047857 55%, #065f46 100%); }
  .kpi-card__title { color: rgba(255, 255, 255, 0.92); text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); }
  .cc-hero__value, .cc-split__legend { text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); }
  .cc-hero__meta { opacity: 1; color: rgba(255, 255, 255, 0.9); }
}
.cc-hero__body { position: relative; z-index: 2; width: 100%; min-width: 0; }
.cc-hero__value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cc-hero__meta {
  display: flex;
  gap: 1.2rem;
  margin-top: 0.2rem;
  font-size: 0.82rem;
  opacity: 0.92;
  flex-wrap: wrap;
}
.cc-split { margin-top: 0.65rem; }
.cc-split__bar {
  height: 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.38);
  overflow: hidden;
  /* sunken track */
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.35), inset 0 -1px 0 rgba(255, 255, 255, 0.12);
}
.cc-split__seg--sales {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #ffd27f, #ffb020);
  /* glossy pill sitting IN the track */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -2px 3px rgba(0, 0, 0, 0.18);
  transition: width 0.4s ease;
}
.cc-split__legend {
  display: flex;
  justify-content: space-between;
  margin-top: 0.45rem;
  font-size: 0.8rem;
  font-weight: 700;
  opacity: 0.95;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.15rem 1.2rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  /* 3D lighting: top-edge highlight (inset) + ambient + key-light shadows */
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      inset 0 -14px 24px -18px rgba(0, 0, 0, 0.35),
      0 16px 32px -16px rgba(20, 24, 50, 0.4),
      0 3px 6px rgba(20, 24, 50, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 150px;

  /* calm hover: a small lift, no tilt/scale */
  &.kpi-card--click:hover {
    transform: translateY(-2px);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.32),
        inset 0 -14px 24px -18px rgba(0, 0, 0, 0.32),
        0 20px 36px -18px rgba(20, 24, 50, 0.45),
        0 4px 8px rgba(20, 24, 50, 0.1);
  }
  &.kpi-card--click:active {
    transform: translateY(0);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
    font-size: 1.15rem;
    /* raised chip: light from above, soft drop below */
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.45),
        inset 0 -2px 4px rgba(0, 0, 0, 0.18),
        0 4px 8px -3px rgba(0, 0, 0, 0.28);
  }
  &__body { position: relative; z-index: 2; flex: 1; min-width: 0; }
  &__title {
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.92;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__value {
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* secondary figures (amount / count / leads): readable, not a footnote */
  &__footer {
    margin-top: 0.45rem;
    font-size: 0.98rem;
    font-weight: 600;
    opacity: 0.96;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__footer + &__footer { margin-top: 0.3rem; }
  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }
  &__expand {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  &--click {
    cursor: pointer;
    &:focus { outline: none; }
    &:hover .kpi-card__expand { opacity: 0.95; transform: scale(1.15); }
  }

  &--primary { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%); }
  &--success { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
  &--warning { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
  &--danger  { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); }
  &--txf     { background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%); }
  &--pending { background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%); }

  &--split {
    padding: 0;
    min-height: 120px;

    .kpi-half {
      position: absolute;
      inset: 0;
      border: none;
      background: transparent;
      color: #fff;
      cursor: pointer;
      z-index: 2;
      padding: 0;
      transition: background 0.18s ease;
      &:focus { outline: none; }
      &--dep {
        clip-path: polygon(0 0, 100% 0, 0 100%);
        background: rgba(255, 255, 255, 0.10);
        &:hover { background: rgba(255, 255, 255, 0.4); }
      }
      &--wd {
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
        background: rgba(0, 0, 0, 0.16);
        &:hover { background: rgba(0, 0, 0, 0.45); }
      }
      &:hover .kpi-half__inner { transform: scale(1.08); }
    }
    .kpi-half__inner {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      transition: transform 0.18s ease;
    }
    .kpi-half--dep .kpi-half__inner { top: 13px; left: 16px; align-items: flex-start; text-align: left; transform-origin: top left; }
    .kpi-half--wd .kpi-half__inner { bottom: 13px; right: 16px; align-items: flex-end; text-align: right; transform-origin: bottom right; }
    .kpi-half__title {
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.92;
      display: inline-flex;
      align-items: center;
    }
    .kpi-half__value { font-size: 1.45rem; font-weight: 800; line-height: 1.1; }
    .kpi-half__amount {
      font-size: 0.82rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      opacity: 0.92;
      white-space: nowrap;
    }
  }
}
.cc-pending { min-height: 128px; }

/* ===== Hero internals: clickable headline, split parts, pending deposits row ===== */
/* Headline row: the inclusive total on the left, the approved-only figure in
   its own glass box on the right. Wraps on narrow cards so neither is clipped. */
.cc-hero__totals {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.cc-hero__approved {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  margin-top: 0.15rem;
  padding: 0.35rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:hover .cc-hero__expand { opacity: 1; }
  &:focus { outline: none; }
}
.cc-hero__approved-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b7f7d4;
}
.cc-hero__approved-value {
  font-size: 1.25rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cc-hero__approved-meta { font-size: 0.72rem; font-weight: 600; opacity: 0.9; white-space: nowrap; }
.cc-hero__main {
  display: block;
  /* shares the headline row with the approved box — grow, never force a wrap */
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;
  &:focus { outline: none; }
  &:hover .cc-hero__expand { opacity: 1; transform: scale(1.1); }
  .kpi-card__title { display: flex; align-items: center; gap: 0.5rem; }
  .cc-hero__value, .cc-hero__meta { display: block; }
  .cc-hero__meta { display: flex; }
}
.cc-hero__expand { font-size: 0.8rem; opacity: 0.6; transition: all 0.15s ease; }
.cc-hero__net {
  margin-top: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:hover .cc-hero__expand { opacity: 1; }
  &:focus { outline: none; }
}
.cc-split__part {
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  &:hover { background: rgba(255, 255, 255, 0.14); }
  &:focus { outline: none; }
}
.cc-split__pct {
  font-size: 0.7rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
}
.cc-hero__pending {
  margin-top: 0.6rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 210, 127, 0.55);
  border-radius: 10px;
  /* dark glass with an amber edge — the pending numbers pop on the green card */
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:focus { outline: none; }
}
.cc-hero__pending { flex-direction: column; align-items: stretch; gap: 0.3rem; }
.cc-hero__pending-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; }
.cc-hero__pending-label { font-size: 0.8rem; font-weight: 700; flex: 1; text-align: left; color: #ffd27f; }
.cc-hero__pending-count { font-size: 1.25rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.cc-hero__pending-amount { font-size: 0.85rem; font-weight: 700; opacity: 0.95; font-variant-numeric: tabular-nums; }
.cc-hero__pending-arrow { font-size: 0.75rem; opacity: 0.75; }

/* Card extras: FTD funnel line, STD share badge */
.cc-kpi-sub { font-size: 0.9rem; font-weight: 600; opacity: 0.95; }
.cc-pct {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-weight: 800;
  font-size: 0.95rem;
}

/* ===== Needs attention — 2x2 tiles ===== */
.cc-watch {
  padding: 0.85rem 0.85rem 0.95rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfe 100%);
  border: 1px solid rgba(34, 41, 47, 0.08);
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 12px 30px -14px rgba(20, 24, 50, 0.22),
      0 2px 6px rgba(20, 24, 50, 0.06);
}
.cc-watch__title {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
  margin-bottom: 0.65rem;
}
.cc-watch__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.6rem;
}
.cc-watch__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid transparent;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 18px -8px rgba(20, 24, 50, 0.35); }
  &:focus { outline: none; }
  &--wd {
    background: rgba(234, 84, 85, 0.1);
    border-color: rgba(234, 84, 85, 0.35);
    .cc-watch__label, .cc-watch__amount { color: #d64545; }
  }
  &--review {
    background: rgba(255, 159, 67, 0.12);
    border-color: rgba(255, 159, 67, 0.4);
    .cc-watch__label, .cc-watch__amount { color: #c47b28; }
  }
  &--fake {
    background: repeating-linear-gradient(135deg, rgba(240, 134, 43, 0.1) 0 8px, rgba(240, 134, 43, 0.05) 8px 16px);
    border: 1px dashed rgba(240, 134, 43, 0.7);
    .cc-watch__label, .cc-watch__amount { color: #c9641a; }
  }
}
.cc-watch__badge {
  position: absolute;
  top: 6px;
  right: 8px;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.cc-watch__label {
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.cc-watch__count {
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #24292f;
}
.cc-watch__amount { font-size: 0.82rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.dark-layout {
  .cc-watch { background: linear-gradient(180deg, #283046 0%, #242b3d 100%); border-color: rgba(255, 255, 255, 0.08); }
  .cc-watch__title { color: #b4b7bd; }
  .cc-watch__count { color: #d0d2d6; }
}

@media (max-width: 768px) {
  .cc-hero__value { font-size: 1.9rem; }
  .kpi-card { min-height: 120px; }
}

/* privacy eye (Agent Insights) */
.ui-masked { filter: blur(6px); user-select: none; }
</style>
